// Client container page — commonsku-style layout (locked 2026-05-29
// Nav Part 2). Replaces the prior right-side Tabs implementation
// (~1,500 lines) with a routed LEFT sub-nav matching ProjectDetailPage:
//
// Structure:
//   - Breadcrumb (← Clients)
//   - Header (Client / {name} / status pill + credit-hold pill)
//   - Left sub-nav: Overview / Activity / Contacts / Addresses / Projects / Art (route /library)
//   - Main column: tab content via <Outlet context={...}/>
//
// Each tab is a separate route (`/clients/:id/{tab}`) so browser back/
// forward works and tab state is shareable/bookmarkable. The /clients/:id
// bare URL renders the Overview tab at the index route (no redirect —
// mirrors the ProjectDetailLayout pattern).
//
// Outlet context shape: `{ client, canEdit, clientId }` — read via
// `useOutletContext<ClientDetailContext>()` in each tab. Per-tab data
// (contacts, addresses, projects, touchpoints, files) is fetched
// inside the tab so navigation away unmounts the query.
import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  ArrowLeft,
  Activity,
  Building2,
  Contact,
  FolderKanban,
  Library,
  MapPin,
  Store,
  User,
} from "lucide-react";
import { Fact, FactsRow } from "@/components/ui/RecordParts";
import {
  accountDeleteBlockReason,
  DELETE_RECOVERABILITY_NOTE,
} from "@merchd/shared";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { DeleteEntityAction } from "@/components/common/DeleteEntityAction";
import {
  StatusPill,
  clientStatusTone,
} from "@/components/ui/StatusPill";
import { useAuth } from "@/auth/AuthProvider";
import { cn, timeAgo } from "@/lib/utils";

// Roles permitted to edit a client. Mirrors the value used by the
// original ClientDetailPage (Phase 7.5.1).
const EDIT_ROLES = ["admin", "client_rep"] as const;

export interface ClientDetailResponse {
  id: string;
  name: string;
  status: string | null;
  clientRepId: string | null;
  onCreditHold: boolean | null;
  creditHoldReason: string | null;
  notes: string | null;
  // CRM batch (2026-07-24) — vertical + sub-account parent + child count.
  industry: string | null;
  /** 2026-10-13 — no sales tax on this client's Flow quotes. */
  taxExempt?: boolean;
  taxExemptNote?: string | null;
  parentClientId: string | null;
  contactCount: number;
  subAccountCount: number;
  // CEO 2026-07-29 — active (non-deleted) projects; feeds the delete pre-disable
  // reason (an account with live projects can't be deleted until they're gone).
  activeProjectCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  /** How the client found us (CEO 2026-10-21). Absent / leadSourceReady=false
   *  until the database step runs — then the field is hidden. null = unknown. */
  leadSourceReady?: boolean;
  /** Tax-exempt certificate file (Stephanie 2026-10-22). Ready=false until
   *  Jake's database step runs — then only the number box shows. */
  taxExemptCertificate?: { fileId: string; filename: string; url: string | null } | null;
  taxExemptCertificateReady?: boolean;
  /** From the client's normal address (Stephanie 2026-11-02). */
  taxLocation?: { state: string | null; status: "exempt" | "out_of_state" | "florida" | "unknown" | string };
  leadSource?: string | null;
  leadSourceDetail?: string | null;
  leadSourceSetAt?: string | null;
  leadSourceSetByName?: string | null;
}

export interface ClientDetailContext {
  client: ClientDetailResponse;
  canEdit: boolean;
  clientId: string;
}

type IconType = typeof FolderKanban;

interface SubTab {
  to: string;
  label: string;
  icon: IconType;
  ready: boolean;
}

const TABS: SubTab[] = [
  { to: "overview", label: "Overview", icon: User, ready: true },
  { to: "activity", label: "Activity", icon: Activity, ready: true },
  { to: "contacts", label: "Contacts", icon: Contact, ready: true },
  { to: "addresses", label: "Addresses", icon: MapPin, ready: true },
  { to: "projects", label: "Projects", icon: FolderKanban, ready: true },
  // CRM batch (2026-07-24) — Sub-Accounts (Account → Sub-Account, one level).
  { to: "subaccounts", label: "Sub-Accounts", icon: Building2, ready: true },
  // Company stores (S2, CEO 2026-10-12) — this client's store(s).
  { to: "store", label: "Store", icon: Store, ready: true },
  // The account's art: its art library + its old commonsku art (Stephanie +
  // reps, Jake go 2026-11-02). Route stays /library for old links.
  { to: "library", label: "Art", icon: Library, ready: true },
];

export function ClientDetailLayout() {
  const { id = "" } = useParams<{ id: string }>();
  const { user } = useAuth();
  const canEdit =
    user !== null && EDIT_ROLES.some((r) => user.roles.includes(r));

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["clients", id, "detail"],
    queryFn: () => api<{ client: ClientDetailResponse }>(`/clients/${id}`),
    enabled: id.length > 0,
  });
  // Opening an account puts it at the top of your Recent list on the Accounts
  // page (Stephanie 2026-11-02). Best-effort; once per account opened.
  const qc = useQueryClient();
  const loadedId = data?.client.id;
  useEffect(() => {
    if (!loadedId) return;
    void api<{ recorded: boolean }>(`/clients/${loadedId}/viewed`, { method: "POST" })
      .then((r) => { if (r.recorded) void qc.invalidateQueries({ queryKey: ["clients", "recent"] }); })
      .catch(() => {});
  }, [loadedId, qc]);
  // A sub-account's breadcrumb links back to its main account (Jake
  // 2026-10-30: "I can't go back to the main account from a sub account";
  // the clickable name is enough — no separate Back link).
  const parentId = data?.client.parentClientId ?? null;
  const parentQuery = useQuery({
    queryKey: ["clients", parentId, "detail"],
    queryFn: () => api<{ client: ClientDetailResponse }>(`/clients/${parentId}`),
    enabled: parentId !== null,
  });
  const parentName = parentQuery.data?.client.name ?? "Main account";

  if (isLoading) {
    return <div className="mt-4 h-24 animate-pulse rounded-md bg-white" aria-label="Loading account" />;
  }
  if (isError || !data?.client) {
    const is404 = error instanceof ApiError && error.status === 404;
    return (
      <div className="mx-auto max-w-3xl py-20 text-center">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          {is404 ? "Account not found" : "Couldn't load account"}
        </h1>
        <Button asChild variant="ghost" className="mt-6">
          <Link to="/clients">
            <ArrowLeft className="h-4 w-4" /> Back to accounts
          </Link>
        </Button>
      </div>
    );
  }

  const c = data.client;
  const ctx: ClientDetailContext = { client: c, canEdit, clientId: id };

  return (
    <div className="mx-auto w-full">
      {/* Record header (handoff "Record workspace"): context line, 32px name,
          status, a facts row — then routed tabs. Deep links restore the tab. */}
      <div className="mb-6 mt-2 flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            <Link to="/clients" className="hover:text-ink">
              Accounts
            </Link>{" "}
            /{" "}
            {c.parentClientId ? (
              <>
                <Link to={`/clients/${c.parentClientId}`} className="hover:text-ink">
                  {parentName}
                </Link>{" "}
                /{" "}
                {/* Back to the main account's sub-account list (Jake 2026-10-30). */}
                <Link to={`/clients/${c.parentClientId}/subaccounts`} className="hover:text-ink">
                  Sub-Account
                </Link>{" "}
                / <span className="text-ink">{c.name}</span>
              </>
            ) : (
              <span>Account</span>
            )}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
              {c.name}
            </h1>
            <StatusPill tone={clientStatusTone(c.status)}>
              {c.status ?? "unknown"}
            </StatusPill>
            {c.onCreditHold && (
              <StatusPill tone="warn">On credit hold</StatusPill>
            )}
          </div>
          <FactsRow>
            <Fact label="Industry">{c.industry ?? "Not set"}</Fact>
            <Fact label="Active projects">{c.activeProjectCount.toLocaleString()}</Fact>
            <Fact label="Contacts">{c.contactCount.toLocaleString()}</Fact>
            {c.subAccountCount > 0 && (
              <Fact label="Sub-accounts">{c.subAccountCount.toLocaleString()}</Fact>
            )}
            <Fact label="Last updated">{timeAgo(c.updatedAt)}</Fact>
          </FactsRow>
        </div>

        {/* Admin-only delete — the account's one destructive action. Soft delete
            (recoverable by an operator); non-admins never see it. PRE-DISABLED
            with an honest reason when the account still has active sub-accounts,
            because softDeleteClient REFUSES (409) rather than orphan them — the
            same decision the server enforces (accountDeleteBlockReason). */}
        <div className="flex shrink-0 items-center">
          <DeleteEntityAction
            deletePath={`/clients/${c.id}`}
            triggerLabel="Delete account"
            confirmTitle="Delete this account?"
            confirmBody={
              <>
                This removes{" "}
                <strong className="font-semibold text-brown-500">
                  {c.name}
                </strong>{" "}
                from the CRM. {DELETE_RECOVERABILITY_NOTE}
              </>
            }
            blockReason={accountDeleteBlockReason({
              subAccountCount: c.subAccountCount,
              activeProjectCount: c.activeProjectCount,
            })}
            redirectTo="/clients"
            flashMessage={`Account “${c.name}” deleted.`}
            errorFallback="Couldn't delete this account. Refresh and try again."
            invalidateKeys={[["clients"]]}
          />
        </div>
      </div>

      {/* Routed record tabs — one row, scrolls sideways on a phone. */}
      <nav
        aria-label="Account sections"
        className="mb-6 flex gap-x-6 overflow-x-auto overflow-y-hidden border-b border-rule"
      >
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              cn(
                "-mb-px inline-flex min-h-[44px] shrink-0 items-center whitespace-nowrap border-b-2 px-0.5 text-sm transition-colors",
                isActive
                  ? "border-ink font-medium text-ink"
                  : "border-transparent text-muted hover:text-ink",
              )
            }
          >
            {t.label}
            {!t.ready && <span className="ml-1.5 text-[11px] text-[#8a8a85]">Soon</span>}
          </NavLink>
        ))}
      </nav>

      <div className="min-w-0">
        <Outlet context={ctx satisfies ClientDetailContext} />
      </div>
    </div>
  );
}
